import { queryOptions } from '@tanstack/react-query';

import config from 'config';
import { createAPIError } from 'utils/exceptions/apiError';

import { readChallengeHash } from '../otp';

export type VatOption = {
  key: string;
  label: string;
};

type VatOptionsVariables = {
  countryCode: string;
  /** The email whose challenge holds the hash the request sends. */
  businessEmail: string;
};

type FetchVatOptionsParams = VatOptionsVariables & {
  emailValidationId: string | undefined;
};

async function fetchVatOptions({
  countryCode,
  emailValidationId,
  businessEmail,
}: FetchVatOptionsParams): Promise<VatOption[]> {
  const params = new URLSearchParams({
    country: countryCode,
    businessEmail,
  });

  if (emailValidationId) {
    params.append('emailValidationId', emailValidationId);
  }

  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/signup/vats?${params}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw await createAPIError(response);
  }

  return response.json();
}

/**
 * `GET /v1/signup/vats` is a protected route, so it still sends the hash. Its answer depends only on the country, so the key holds the country only. The hash and the email are credentials: the query function reads the hash of the email's challenge when it runs.
 */
export function getVatOptionsQueryOptions({
  countryCode,
  businessEmail,
}: VatOptionsVariables) {
  return queryOptions({
    // The email only finds the hash. It never changes what the lookup answers.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['vat-options', countryCode],
    queryFn: ({ client }) =>
      fetchVatOptions({
        countryCode,
        businessEmail,
        emailValidationId: readChallengeHash({
          queryClient: client,
          email: businessEmail,
        }),
      }),
  });
}
